import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Loader from '../components/Loader';
import { FaUserCircle } from "react-icons/fa";
import '../styles/Plan.css';


const Plans = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    const [creating, setCreating] = useState(false);
    const [editingKey, setEditingKey] = useState(null); // null if not editing
    const [newPlan, setNewPlan] = useState({
        name: '',
        price: '',
        amount: '',
        duration: '',
        durationDisplay: '',
        profit: '',
        hardware: '',
        efficiency: '',
        chains: '',
        tps: '',
        maxTps: '',
        specs: '' // multiline text input, split into array on save
    });
    const [saving, setSaving] = useState(false);

    // Fetch user role
    useEffect(() => {
        const fetchCurrentUserRole = async () => {
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                setCurrentUserRole("unauthenticated");
                setAuthLoading(false);
                return;
            }

            try {
                const currentUserDoc = await getDoc(doc(db, "users", currentUser.uid));
                if (currentUserDoc.exists()) {
                    const role = currentUserDoc.data().role || "user";
                    setCurrentUserRole(role);
                } else {
                    setCurrentUserRole("unknown");
                }
            } catch (error) {
                console.error("Error fetching current user role:", error);
                setCurrentUserRole("error");
            } finally {
                setAuthLoading(false);
            }
        };

        fetchCurrentUserRole();
    }, []);

    // Fetch plans
    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const docRef = doc(db, 'users', 'plans');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setPlans(docSnap.data() || {});
                } else {
                    setPlans({});
                }
            } catch (error) {
                console.error("Error fetching plans:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    const resetForm = () => {
        setNewPlan({
            name: '',
            price: '',
            amount: '',
            duration: '',
            durationDisplay: '',
            profit: '',
            hardware: '',
            efficiency: '',
            chains: '',
            tps: '',
            maxTps: '',
            specs: ''
        });
        setEditingKey(null);
        setCreating(false);
    }

    const handleCreateClick = () => {
        resetForm();
        setCreating(true);
    };

    const handleEditClick = (key) => {
        const plan = plans[key];
        if (!plan) return;
        setNewPlan({
            name: plan.name || '',
            price: plan.price || '',
            amount: plan.amount || '',
            duration: plan.duration || '',
            durationDisplay: plan.durationDisplay || '',
            profit: plan.profit || '',
            hardware: plan.hardware || '',
            efficiency: plan.efficiency || '',
            chains: plan.chains || '',
            tps: plan.tps || '',
            maxTps: plan.maxTps || '',
            specs: (plan.specs || []).join('\n')
        });
        setEditingKey(key);
        setCreating(true);
    };

    const handleDeleteClick = async (key) => {
        if (!window.confirm("Are you sure you want to delete this plan?")) return;

        const updatedPlans = { ...plans };
        delete updatedPlans[key];

        const docRef = doc(db, 'users', 'plans');

        try {
            await updateDoc(docRef, updatedPlans);
            setPlans(updatedPlans);
            // If currently editing this plan, close form
            if (editingKey === key) {
                resetForm();
            }
        } catch (error) {
            console.error("Error deleting plan:", error);
            alert("Failed to delete plan");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPlan(prev => ({ ...prev, [name]: value }));
    };

    const saveNewPlan = async () => {
        if (!newPlan.name.trim()) {
            alert("Plan name is required");
            return;
        }
        setSaving(true);

        // If editing, use editingKey; else create new key
        let keyToUse = editingKey;
        if (!keyToUse) {
            // Determine next plan key, like plan1, plan2, ...
            const existingKeys = Object.keys(plans)
                .filter(k => /^plan\d+$/.test(k))
                .map(k => parseInt(k.replace('plan', ''), 10));
            const nextNumber = existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 1;
            keyToUse = `plan${nextNumber}`;
        }

        // Prepare specs array by splitting lines and filtering out empty
        const specsArray = newPlan.specs
            .split('\n')
            .map(s => s.trim())
            .filter(s => s.length > 0);

        // Prepare the full plan data
        const planData = {
            name: newPlan.name.trim(),
            price: newPlan.price.trim(),
            amount: Number(newPlan.amount) || 0,
            duration: Number(newPlan.duration) || 0,
            durationDisplay: newPlan.durationDisplay.trim(),
            profit: Number(newPlan.profit) || 0,
            hardware: newPlan.hardware.trim(),
            efficiency: newPlan.efficiency.trim(),
            chains: newPlan.chains.trim(),
            tps: newPlan.tps.trim(),
            maxTps: Number(newPlan.maxTps) || 0,
            specs: specsArray
        };

        const updatedPlans = {
            ...plans,
            [keyToUse]: planData
        };

        const docRef = doc(db, 'users', 'plans');

        try {
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                await updateDoc(docRef, updatedPlans);
            } else {
                await setDoc(docRef, updatedPlans);
            }

            setPlans(updatedPlans);
            resetForm();
        } catch (error) {
            console.error("Error saving plan:", error);
            alert("Failed to save plan");
        } finally {
            setSaving(false);
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    if (loading || authLoading) return <Loader />;

    if (currentUserRole !== "admin") {
        return (
            <div className="web3-user-not-found">
                <FaUserCircle size={48} />
                <h2>Access Denied</h2>
                <div onClick={handleGoBack} className="route-Links">
                    Go Back
                </div>
            </div>
        );
    }

    // Sort plans by their number suffix, ascending
    const sortedPlanEntries = Object.entries(plans).sort((a, b) => {
        const numA = parseInt(a[0].replace('plan', ''), 10);
        const numB = parseInt(b[0].replace('plan', ''), 10);
        return numA - numB;
    });

    return (
        <div className="plan-container">
            <h2>All Plans</h2>

            {Object.keys(plans).length === 0 && !creating && (
                <p>Nothing to show.</p>
            )}

            {creating && (
                <div className="modal-overlay" onClick={resetForm}>
                    <div className="plan-create-form" onClick={e => e.stopPropagation()}>
                        <div className="plan-modal-body">
                            <div>
                                <label htmlFor="">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    className='input-field'
                                    placeholder="Plan Name"
                                    value={newPlan.name}
                                    onChange={handleInputChange}
                                    disabled={saving}
                                />
                            </div>
                            <div className='plan-form-grid'>

                                <div>
                                    <label htmlFor="">Price (eg. $1,000)</label>
                                    <input
                                        type="text"
                                        className='input-field'
                                        name="price"
                                        placeholder="Price (e.g. $1,000)"
                                        value={newPlan.price}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Amount (eg 1000)</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        className='input-field'
                                        placeholder="Amount (e.g. 1000)"
                                        value={newPlan.amount}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Duration (eg. 3)</label>
                                    <input
                                        type="number"
                                        className='input-field'
                                        name="duration"
                                        placeholder="Duration (number eg. 3)"
                                        value={newPlan.duration}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Duration Display (eg. 5 Days)</label>
                                    <input
                                        type="text"
                                        className='input-field'
                                        name="durationDisplay"
                                        placeholder="Duration Display (e.g. 5 days)"
                                        value={newPlan.durationDisplay}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Profit (eg. 85)</label>
                                    <input
                                        type="number"
                                        className='input-field'
                                        name="profit"
                                        placeholder="Profit (e.g. 85)"
                                        value={newPlan.profit}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Description</label>
                                    <input
                                        type="text"
                                        className='input-field'
                                        name="hardware"
                                        placeholder="Hardware Description"
                                        value={newPlan.hardware}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Efficiency (eg.85% Uptime)</label>
                                    <input
                                        type="text"
                                        className='input-field'
                                        name="efficiency"
                                        placeholder="Efficiency (e.g. 85% Uptime)"
                                        value={newPlan.efficiency}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Chains (eg. Sol, Eth)</label>
                                    <input
                                        type="text"
                                        className='input-field'
                                        name="chains"
                                        placeholder="Chains (e.g. Eth, Sol, Avalanche)"
                                        value={newPlan.chains}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">TPS (eg. Upto 850 TPS)</label>
                                    <input
                                        type="text"
                                        className='input-field'
                                        name="tps"
                                        placeholder="TPS (e.g. Up to 850 TPS)"
                                        value={newPlan.tps}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="">Max TPS (eg. 850)</label>
                                    <input
                                        type="number"
                                        className='input-field'
                                        name="maxTps"
                                        placeholder="Max TPS (e.g. 850)"
                                        value={newPlan.maxTps}
                                        onChange={handleInputChange}
                                        disabled={saving}
                                    />
                                </div>

                            </div>

                            <div>
                                <label htmlFor="">Specs (One spec per line)</label>
                                <textarea
                                    name="specs"
                                    placeholder="Specs (one per line)"
                                    className='input-field'
                                    value={newPlan.specs}
                                    onChange={handleInputChange}
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        <div className="plan-modal-footer">
                            <button onClick={saveNewPlan} disabled={saving}>
                                {saving ? 'Saving...' : (editingKey ? 'Save Changes' : 'Save')}
                            </button>
                            <button onClick={resetForm} disabled={saving}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!creating && (
                <button className="create-btn" onClick={handleCreateClick}>
                    Create New Plan
                </button>
            )}

            <div className="plan-grid">
                {sortedPlanEntries.map(([key, plan]) => (
                    <div key={key} className="plan-box">
                        <h3>{plan.name}</h3>
                        <p>Price: {plan.price}</p>
                        <p>Amount: {plan.amount}</p>
                        <p>Duration: {plan.durationDisplay}</p>
                        <p>Profit: {plan.profit}%</p>
                        <p>Description: {plan.hardware}</p>
                        <p>Efficiency: {plan.efficiency}</p>
                        <p>Category: {plan.chains}</p>
                        <p>TPS: {plan.tps}</p>
                        <p>Extra TPS: {plan.maxTps}</p>
                        <ul>
                            {plan.specs?.map((spec, idx) => (
                                <li key={idx}>{spec}</li>
                            ))}
                        </ul>
                        <div className="plan-actions">
                            <button onClick={() => handleEditClick(key)} disabled={saving}>
                                Edit
                            </button>
                            <button onClick={() => handleDeleteClick(key)} disabled={saving}>
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Plans;
